import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CalendarDaysIcon,
  ExclamationTriangleIcon,
  WrenchScrewdriverIcon,
} from '@heroicons/react/24/outline';
import technicalMaintenanceApi from '../../api/technicalMaintenanceApi';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Label } from '../../components/ui/Label';
import { Select } from '../../components/ui/Select';
import {
  TechnicalMaintenancePageShell,
  MaintenanceListCard,
  EmptyState,
  formatDisplayDate,
  daysLabel,
  scheduleBadgeClass,
} from './technicalMaintenanceUi';

const defaultFilters = {
  days_ahead: '90',
  area: '',
};

const TechnicalMaintenanceSchedulePage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [schedule, setSchedule] = useState(null);
  const [areas, setAreas] = useState([]);
  const [filters, setFilters] = useState(defaultFilters);
  const [activeTab, setActiveTab] = useState('overdue');

  const loadData = async (activeFilters = filters) => {
    setLoading(true);
    setError(null);
    try {
      const [scheduleResponse, filterOptions] = await Promise.all([
        technicalMaintenanceApi.getMaintenanceSchedule({
          days_ahead: activeFilters.days_ahead,
          area: activeFilters.area,
        }),
        technicalMaintenanceApi.getEquipmentFilterOptions(),
      ]);
      setSchedule(scheduleResponse?.data || scheduleResponse);
      setAreas(filterOptions?.data?.areas || filterOptions?.areas || []);
    } catch (err) {
      setError(err.message || 'Bakım takvimi yüklenemedi.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const tabRows = useMemo(() => {
    if (!schedule) return [];
    if (activeTab === 'overdue') return schedule.overdue || [];
    if (activeTab === 'upcoming') return schedule.upcoming || [];
    return schedule.planned || [];
  }, [schedule, activeTab]);

  const tabs = [
    { id: 'overdue', label: 'Geciken', count: schedule?.overdue_count ?? 0, icon: ExclamationTriangleIcon, className: 'overdue' },
    { id: 'upcoming', label: 'Yaklaşan', count: schedule?.upcoming_count ?? 0, icon: CalendarDaysIcon },
    { id: 'planned', label: 'Planlanan', count: schedule?.planned_count ?? 0, icon: WrenchScrewdriverIcon },
  ];

  const emptyMessages = {
    overdue: 'Geciken bakım yok — harika!',
    upcoming: 'Seçilen süre içinde yaklaşan bakım bulunmuyor.',
    planned: 'Planlanmış bakım kaydı yok.',
  };

  return (
    <TechnicalMaintenancePageShell
      title="Bakım Takvimi"
      subtitle="Geciken, yaklaşan ve planlanmış bakımları tek ekranda takip edin."
      actions={(
        <Button variant="secondary" onClick={() => navigate('/internal-audit/technical-maintenance/maintenance')}>
          Tüm Kayıtlar
        </Button>
      )}
    >
      <div className="tm-toolbar">
        <div className="tm-segmented">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`tm-segment ${tab.className || ''} ${activeTab === tab.id ? 'active' : ''}`}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
                <span className="tm-segment-count">{tab.count}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="tm-inline-filters">
        <div className="field">
          <Label>Pencere (gün)</Label>
          <Input
            type="number"
            min="1"
            max="365"
            value={filters.days_ahead}
            onChange={(e) => setFilters({ ...filters, days_ahead: e.target.value })}
          />
        </div>
        <div className="field wide">
          <Label>Bölüm</Label>
          <Select
            value={filters.area}
            onChange={(e) => setFilters({ ...filters, area: e.target.value })}
          >
            <option value="">Tüm bölümler</option>
            {areas.map((area) => (
              <option key={area} value={area}>{area}</option>
            ))}
          </Select>
        </div>
        <Button onClick={() => loadData(filters)}>Uygula</Button>
        <Button
          variant="secondary"
          onClick={() => {
            setFilters(defaultFilters);
            loadData(defaultFilters);
          }}
        >
          Sıfırla
        </Button>
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-200">
          {error}
        </div>
      )}

      {loading ? (
        <div className="tm-record-list">
          {[1, 2, 3].map((i) => <div key={i} className="tm-skeleton" />)}
        </div>
      ) : tabRows.length === 0 ? (
        <EmptyState message={emptyMessages[activeTab]} />
      ) : (
        <div className="tm-record-list">
          {tabRows.map((row) => {
            const badgeClass = scheduleBadgeClass(row.days_until, row.schedule_kind);
            const badgeLabel = row.schedule_kind === 'planned'
              ? 'Planlandı'
              : daysLabel(row.days_until);
            const excerpt = [row.description, row.replaced_parts].filter(Boolean).join(' · ');

            return (
              <MaintenanceListCard
                key={`${row.id}-${row.schedule_kind}`}
                title={row.equipment_name}
                subtitle={row.maintenance_type_label}
                area={row.equipment_area}
                date={formatDisplayDate(row.schedule_date)}
                badgeLabel={badgeLabel}
                badgeClass={badgeClass}
                excerpt={excerpt || undefined}
                onClick={() => {
                  if (row.equipment) {
                    navigate(`/internal-audit/technical-maintenance/equipment/${row.equipment}`);
                  }
                }}
              />
            );
          })}
        </div>
      )}
    </TechnicalMaintenancePageShell>
  );
};

export default TechnicalMaintenanceSchedulePage;
